import { useEffect, useState } from "react";
import { History, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { lineDiff } from "@/lib/diff";
import { pageDoc, pageHtml } from "@/lib/blocks";
import { useWorld } from "@/context/WorldContext";

const fmt = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "medium" });

const FIELDS = { title: "title", category: "category", summary: "summary", content: "content", image: "image", tags: "tags", event_date: "date", event_order: "order", sections: "facts", gallery: "gallery" };
const formatOnly = (a, b) => pageHtml(a) !== pageHtml(b) && pageDoc(a) === pageDoc(b);
const norm = (r) => ({ ...r, content: pageHtml(r) });
const changedFields = (x, y) => { const [a, b] = [norm(x), norm(y)]; return Object.keys(FIELDS).filter((k) => JSON.stringify(a[k] ?? (k === "gallery" ? [] : null)) !== JSON.stringify(b[k] ?? (k === "gallery" ? [] : null)))
  .map((k) => (k === "content" && formatOnly(x, y) ? "formatting" : FIELDS[k])); };

export const DiffView = ({ from, to }) => (
  <pre className="font-code text-[11px] leading-relaxed whitespace-pre-wrap rounded-lg bg-canvas border border-line p-3 max-h-[45vh] overflow-y-auto" data-testid="revision-diff">
    {lineDiff(from, to).map((l, i) => (
      <div key={i} className={l.t === "add" ? "bg-emerald-500/10 text-emerald-300" : l.t === "del" ? "bg-rose-500/10 text-rose-300 line-through decoration-rose-500/40" : "text-slate-500"}>
        {l.t === "add" ? "+ " : l.t === "del" ? "- " : "  "}{l.v || " "}
      </div>
    ))}
  </pre>
);

export const PageHistory = ({ page }) => {
  const { reload } = useWorld();
  const [open, setOpen] = useState(false);
  const [revs, setRevs] = useState(null);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRevs(null);
    setSel(null);
    api.get(`/pages/${page.id}/revisions`).then((r) => { setRevs(r.data); setSel(r.data[0] || null); })
      .catch((e) => { toast.error(formatApiError(e)); setRevs([]); });
  }, [open, page.id, page.updated_at]);

  const restore = async () => {
    setBusy(true);
    try {
      await api.post(`/pages/${page.id}/revisions/${sel.id}/restore`);
      await reload();
      toast.success(`Restored the version from ${fmt(sel.created_at)}`);
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button className="inline-flex items-center gap-1.5 text-xs rounded-full border border-line px-3 py-1.5 text-slate-300 hover:border-amber-500/40 hover:text-amber-300 transition-colors" data-testid="page-history-btn">
          <History className="w-3 h-3" /> History
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="bg-panel border-line w-full sm:max-w-2xl overflow-y-auto" data-testid="page-history-sheet">
        <SheetTitle className="font-display text-3xl text-amber-100">Page history</SheetTitle>
        <SheetDescription>Every save keeps the previous draft. Pick one to compare or restore.</SheetDescription>
        {revs === null ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-amber-500" /></div>
        ) : revs.length === 0 ? (
          <p className="py-12 text-sm text-slate-500 italic" data-testid="no-revisions">No earlier drafts yet. Older versions appear here after you edit this page.</p>
        ) : (
          <div className="grid sm:grid-cols-[200px_1fr] gap-5 mt-6">
            <ol className="space-y-1 sm:max-h-[70vh] overflow-y-auto" data-testid="revision-list">
              <li className="px-3 py-2 rounded-md text-xs text-emerald-300 border border-emerald-500/30 bg-emerald-500/5">Current · {fmt(page.updated_at)}</li>
              {revs.map((r) => (
                <li key={r.id}>
                  <button onClick={() => setSel(r)} data-testid={`revision-item-${r.id}`}
                    className={`w-full text-left px-3 py-2 rounded-md border-l-2 transition-colors ${sel?.id === r.id ? "bg-amber-500/10 border-amber-500 text-amber-200" : "border-transparent text-slate-400 hover:bg-veil/[0.03] hover:text-slate-100"}`}>
                    <span className="block text-xs">{fmt(r.created_at)}</span>
                    <span className="block text-[11px] text-slate-500 truncate">{r.reason === "restore" ? "Before a restore" : (changedFields(r, page).length ? `Differs in: ${changedFields(r, page).join(", ")}` : "Same as current")}</span>
                  </button>
                </li>
              ))}
            </ol>
            {sel && (
              <div className="space-y-4 min-w-0" data-testid="revision-detail">
                <div>
                  <p className="font-code text-[10px] uppercase tracking-widest text-slate-500">{sel.category}</p>
                  <h3 className="font-display text-2xl text-amber-50" data-testid="revision-title">{sel.title}</h3>
                  {sel.summary && <p className="text-sm italic text-slate-400">{sel.summary}</p>}
                </div>
                <p className="text-xs text-slate-500">Changes from this draft to the current page: <span className="text-rose-300">- removed</span> · <span className="text-emerald-300">+ added</span></p>
                {formatOnly(sel, page) ? <p className="text-xs text-slate-400 italic rounded-lg border border-line p-3" data-testid="revision-format-only">Formatting changed. The words are the same as the current page.</p>
                  : pageHtml(sel) === pageHtml(page)
                  ? <p className="text-xs text-slate-500 italic rounded-lg border border-line p-3" data-testid="revision-same-content">{changedFields(sel, page).length ? `Content is identical to the current page. Differs in: ${changedFields(sel, page).join(", ")}.` : "This draft is identical to the current page."}</p>
                  : <DiffView from={pageDoc(sel)} to={pageDoc(page)} />}
                <Button onClick={restore} disabled={busy} className="rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent font-semibold" data-testid="restore-revision-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Restore this version
                </Button>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
